import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../lib/socket';
import { useAuthStore } from '../store/authStore';
import {
  fetchMessages,
  postMessage,
  markAsRead as markAsReadApi,
  type ChatMessage,
} from '../services/chatService';

const MESSAGE_LIMIT = 50;

// ─── Context Value ───

interface ChatContextValue {
  projectId: string;
  messages: ChatMessage[];
  isLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  sending: boolean;
  sendMessage: (content: string, mentions?: Array<{ userId: string; name: string; offset: number; length: number }>) => Promise<void>;
  loadMore: () => void;
  markAsRead: () => void;
  /** 새 메시지 수신 시 호출되는 콜백 등록 */
  onNewMessage: (cb: NewMessageCallback) => () => void;
  /** 채팅 화면 진입 시 호출 (소켓 룸 참가 + 메시지 로드) */
  enterChat: () => void;
  /** 채팅 화면 이탈 시 호출 (소켓 룸 퇴장) */
  leaveChat: () => void;
}

type NewMessageCallback = (msg: ChatMessage, isMine: boolean) => void;

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return ctx;
}

// ─── Provider ───

export function ChatProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const userId = useAuthStore((s) => s.user?.id);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);

  // 새 메시지 콜백 리스너 (스크롤 제어용)
  const [listeners] = useState<Set<NewMessageCallback>>(() => new Set());

  // ─── 채팅 뷰어 카운트: 실제로 채팅을 보고 있는 컴포넌트 수 ───
  const viewerCountRef = useRef(0);
  const [isViewing, setIsViewing] = useState(false);

  const enterChat = useCallback(() => {
    viewerCountRef.current += 1;
    if (viewerCountRef.current === 1) setIsViewing(true);
  }, []);

  const leaveChat = useCallback(() => {
    viewerCountRef.current = Math.max(0, viewerCountRef.current - 1);
    if (viewerCountRef.current === 0) setIsViewing(false);
  }, []);

  const onNewMessage = useCallback(
    (cb: NewMessageCallback) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    [listeners],
  );

  // ─── 채팅 진입 시: 메시지 로드 + 소켓 룸 참가 ───
  useEffect(() => {
    if (!isViewing) {
      // 채팅을 보고 있지 않으면 초기 상태로
      setIsLoading(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setMessages([]);
    setHasMore(true);
    setNextCursor(null);

    // 메시지 로드
    fetchMessages(projectId, { limit: MESSAGE_LIMIT })
      .then((data) => {
        if (cancelled) return;
        setMessages(data.messages);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setIsLoading(false);
      })
      .catch((err) => {
        console.warn('[Chat] fetchMessages failed:', err);
        if (!cancelled) setIsLoading(false);
      });

    // 읽음 처리
    markAsReadApi(projectId).catch((err) => console.warn('[Chat] markAsRead failed:', err));

    // 소켓 룸 참가 + 새 메시지 수신
    const socket = getSocket();
    if (socket) {
      socket.emit(SOCKET_EVENTS.CHAT_JOIN, { projectId });

      const handleNewMessage = (msg: ChatMessage) => {
        setMessages((prev) => {
          if (prev.some((m) => m.messageId === msg.messageId)) return prev;
          return [...prev, msg];
        });

        const isMine = msg.sender.userId === userId;
        listeners.forEach((cb) => cb(msg, isMine));

        if (isMine) {
          markAsReadApi(projectId).catch(() => {});
        }
      };

      socket.on(SOCKET_EVENTS.CHAT_NEW_MESSAGE, handleNewMessage);

      // 리액션 실시간 업데이트
      const handleReaction = (data: { messageId: string; channelId?: string; reactions: unknown[] }) => {
        setMessages((prev) => prev.map((m) =>
          m.messageId === data.messageId ? { ...m, reactions: data.reactions } as ChatMessage : m,
        ));
      };
      socket.on(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);

      // CHG-123 REQ-V123-014: linkedResources 실시간 갱신
      const handleLinkedResourceUpdated = (data: { messageId: string; channelId?: string; linkedResources: ChatMessage['linkedResources'] }) => {
        setMessages((prev) => prev.map((m) =>
          m.messageId === data.messageId ? { ...m, linkedResources: data.linkedResources } as ChatMessage : m,
        ));
      };
      socket.on(SOCKET_EVENTS.CHAT_MESSAGE_LINKED_RESOURCE_UPDATED, handleLinkedResourceUpdated);

      // CHG-131: 스레드 응답 수신 → 부모 메시지 threadCount/lastThreadAt/threadPreview 업데이트
      // (ChatChannelPage는 ChannelChatContext에서 처리, ProjectChatPage/ChatSidePanel은 이 ChatContext 사용)
      const handleThreadMessage = (data: { parentMessageId: string; message: ChatMessage; threadCount: number }) => {
        setMessages((prev) => prev.map((m) => {
          if (m.messageId !== data.parentMessageId) return m;
          const prevPreview = m.threadPreview ?? [];
          const newSender = data.message.sender;
          const updatedPreview = prevPreview.some((p) => p.userId === newSender.userId)
            ? prevPreview
            : [{ userId: newSender.userId, name: newSender.name, profileImage: newSender.profileImage }, ...prevPreview].slice(0, 3);
          return {
            ...m,
            threadCount: data.threadCount,
            lastThreadAt: data.message.createdAt,
            threadPreview: updatedPreview,
          } as ChatMessage;
        }));
      };
      socket.on(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);

      // CHG-131: 메시지 고정/해제 → isPinned 동기 (ProjectChat/SidePanel에서도 핀 뱃지 즉시 반영)
      const handlePinned = (data: { channelId: string; messageId: string }) => {
        setMessages((prev) => prev.map((m) =>
          m.messageId === data.messageId ? { ...m, isPinned: true } as ChatMessage : m,
        ));
      };
      const handleUnpinned = (data: { channelId: string; messageId: string }) => {
        setMessages((prev) => prev.map((m) =>
          m.messageId === data.messageId ? { ...m, isPinned: false } as ChatMessage : m,
        ));
      };
      socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handlePinned);
      socket.on(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handleUnpinned);

      return () => {
        cancelled = true;
        socket.emit(SOCKET_EVENTS.CHAT_LEAVE, { projectId });
        socket.off(SOCKET_EVENTS.CHAT_NEW_MESSAGE, handleNewMessage);
        socket.off(SOCKET_EVENTS.CHANNEL_REACTION_UPDATED, handleReaction);
        socket.off(SOCKET_EVENTS.CHAT_MESSAGE_LINKED_RESOURCE_UPDATED, handleLinkedResourceUpdated);
        socket.off(SOCKET_EVENTS.CHANNEL_THREAD_MESSAGE, handleThreadMessage);
        socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_PINNED, handlePinned);
        socket.off(SOCKET_EVENTS.CHANNEL_MESSAGE_UNPINNED, handleUnpinned);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [isViewing, projectId, userId, listeners]);

  // ─── 이전 메시지 로드 (무한 스크롤) ───
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    fetchMessages(projectId, { cursor: nextCursor ?? undefined, limit: MESSAGE_LIMIT })
      .then((data) => {
        setMessages((prev) => [...data.messages, ...prev]);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
      })
      .catch((err) => console.warn('[Chat] loadMore failed:', err))
      .finally(() => setLoadingMore(false));
  }, [projectId, nextCursor, hasMore, loadingMore]);

  // ─── 메시지 전송 ───
  const sendMessage = useCallback(
    async (content: string, mentions?: Array<{ userId: string; name: string; offset: number; length: number }>) => {
      setSending(true);
      try {
        const msg = await postMessage(projectId, content, mentions);
        setMessages((prev) => {
          if (prev.some((m) => m.messageId === msg.messageId)) return prev;
          return [...prev, msg];
        });
        // 전송 후 리스너에게도 알림 (자동 스크롤용)
        listeners.forEach((cb) => cb(msg, true));
      } catch {
        // 에러는 axios interceptor가 처리
      } finally {
        setSending(false);
      }
    },
    [projectId, listeners],
  );

  // ─── 읽음 처리 ───
  const markAsRead = useCallback(() => {
    markAsReadApi(projectId).catch((err) => console.warn('[Chat] markAsRead failed:', err));
  }, [projectId]);

  const value = useMemo<ChatContextValue>(
    () => ({
      projectId,
      messages,
      isLoading,
      loadingMore,
      hasMore,
      sending,
      sendMessage,
      loadMore,
      markAsRead,
      onNewMessage,
      enterChat,
      leaveChat,
    }),
    [projectId, messages, isLoading, loadingMore, hasMore, sending, sendMessage, loadMore, markAsRead, onNewMessage, enterChat, leaveChat],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
